import { memo, useEffect, useMemo, useRef } from 'react';
import { FrameworkDataTable as FrameworkDataTableBase } from 'framework-data-table';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import useCrmObjectTypeListingPagination from 'framework-listing-lib/internal/hooks/useCrmObjectTypeListingPagination';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import useCrmObjectTypeListingSort from 'framework-listing-lib/internal/hooks/useCrmObjectTypeListingSort';
import useUserInfo from 'framework-listing-lib/internal/hooks/useUserInfo';
import { useTableColumns } from 'framework-listing-lib/internal/Table/providers/TableColumnsProvider';
import useCrmObjectTypeListingEmptyState from 'framework-listing-lib/internal/hooks/useCrmObjectTypeListingEmptyState';
import { PAGE_SIZE_OPTIONS } from 'framework-listing-lib/constants/pagination';
import { EMPTY_STATE_TYPE } from 'framework-listing-lib/constants';
import FrameworkDataTablePassThroughPropsProvider from 'framework-listing-lib/internal/FrameworkDataTable/providers/FrameworkDataTablePassThroughPropsProvider';
import useShowLoadingSkeleton from 'framework-listing-lib/internal/hooks/useShowLoadingSkeleton';
import useFrameworkDataTableEventHandlers from 'framework-listing-lib/internal/FrameworkDataTable/hooks/useFrameworkDataTableEventHandlers';
import InlineEditProvider from 'framework-listing-lib/internal/FrameworkDataTable/providers/InlineEditProvider';
import { useNumberOfFrozenColumns } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { useIsListMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useUIModeSelectOptions } from 'framework-listing-lib/internal/hooks/useUIModeSelectOptions';
import { useHasSearchInput } from 'framework-listing-lib/internal/hooks/useSearchInputProps';
import ListingLibPropertyHeader from './ListingLibPropertyHeader';
import ListingLibTableHeaderRightSlot from './ListingLibTableHeaderRightSlot';
import ListingLibTableHeaderLeftSlot from './ListingLibTableHeaderLeftSlot';
import ListingLibBulkActionsComponent from './ListingLibBulkActionsComponent';
import ListingLibRowComponent from './ListingLibRowComponent';
import ListingLibFrameworkDataTableSkeleton from './ListingLibFrameworkDataTableSkeleton';
import ListingLibFrameworkDataTableEmptyState from './ListingLibFrameworkDataTableEmptyState';
import ListingLibTableCell from './ListingLibTableCell';
import InlineEditCellTourPopover from './InlineEditCellTourPopover';
import { isTableCondensed } from 'framework-listing-lib/internal/Table/utils/isTableCondensed';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FrameworkDataTable = /*#__PURE__*/memo(FrameworkDataTableBase);
export default function ListingLibFrameworkDataTable({
  actionsComponent,
  bulkActionProps,
  data,
  loading,
  maxHeight = 'full-height',
  pageSizes,
  renderEmptyState,
  renderNestedContent,
  renderPqlRow,
  rowProps,
  withInlineEditCellTour
}) {
  const objectTypeId = useObjectTypeId();
  const emptyStateProps = useCrmObjectTypeListingEmptyState();
  const {
    sortDirection,
    sortKey
  } = useCrmObjectTypeListingSort();
  const {
    hasScope,
    isUngatedFor
  } = useUserInfo();
  const hasEmptyStateRef = useRef(undefined);
  const previousData = useRef(data);
  const previousObjectType = useRef(objectTypeId);
  const {
    page,
    pageSize
  } = useCrmObjectTypeListingPagination(data ? data.total : 0);
  const showLoadingSkeleton = useShowLoadingSkeleton();
  const {
    loading: loadingTableColumns,
    tableColumns
  } = useTableColumns();
  const numberOfFrozenColumns = useNumberOfFrozenColumns();
  const hasSearchInput = useHasSearchInput();
  const {
    shouldDisplaySelect: shouldRenderUIModeSelect
  } = useUIModeSelectOptions();
  const {
    getRowId,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortsChange
  } = useFrameworkDataTableEventHandlers(data);
  const isListMode = useIsListMode();
  const sorts = useMemo(() => [{
    columnNamespace: objectTypeId,
    columnName: sortKey,
    direction: sortDirection
  }], [objectTypeId, sortDirection, sortKey]);
  const columns = useMemo(() => tableColumns.map((col, index) => ({
    columnNamespace: objectTypeId,
    columnName: col.id,
    HeaderContentComponent: ListingLibPropertyHeader,
    CellComponent: ListingLibTableCell,
    width: col.width,
    reorderable: isListMode && Boolean(col.draggable && index !== 0),
    resizable: isListMode && Boolean(col.resizable),
    sortable: Boolean(col.sortable),
    minimumColumnWidth: col.minWidth
  })), [isListMode, objectTypeId, tableColumns]);
  const dataToRender = useMemo(() => {
    if (previousObjectType.current !== objectTypeId) {
      previousObjectType.current = objectTypeId;
      return undefined;
    }
    if (data) {
      previousData.current = data;
      return data;
    }

    /**
     * Keep previous data as placeholder while new data is being fetched.
     */
    if (loading && previousData.current) {
      return previousData.current;
    }
    return undefined;
  }, [data, loading, objectTypeId]);

  /**
   * Find the first valid property that can be inline edited (readOnlyValue = false).
   * This is used later on by InlineEditCellTourPopover so we can highlight at least one editable cell.
   */
  const inlineEditTourPopover = useMemo(() => {
    var _dataToRender$results;
    if (!withInlineEditCellTour) {
      return null;
    }
    const editableColumn = tableColumns.find(tableColumn => tableColumn.order > 0 && tableColumn.propertyDescriptor && !tableColumn.propertyDescriptor.readOnlyValue);
    const editableRowId = dataToRender === null || dataToRender === void 0 || (_dataToRender$results = dataToRender.results[0]) === null || _dataToRender$results === void 0 ? void 0 : _dataToRender$results.objectId;
    if (editableColumn && editableRowId) {
      return /*#__PURE__*/_jsx(InlineEditCellTourPopover, {
        columnName: editableColumn.id,
        rowId: editableRowId
      });
    }
    return null;
  }, [dataToRender === null || dataToRender === void 0 ? void 0 : dataToRender.results, tableColumns, withInlineEditCellTour]);
  const isFirstTimeInApp = useMemo(() => Boolean(emptyStateProps.emptyStateType && emptyStateProps.emptyStateType === EMPTY_STATE_TYPE.FIRST_TIME_IN_APP), [emptyStateProps.emptyStateType]);
  const headerLeftSlot = useMemo(() => {
    if (!isFirstTimeInApp && hasSearchInput) {
      return /*#__PURE__*/_jsx(ListingLibTableHeaderLeftSlot, {});
    }
    return null;
  }, [hasSearchInput, isFirstTimeInApp]);
  const headerRightSlot = useMemo(() => {
    if (!isFirstTimeInApp && (actionsComponent || shouldRenderUIModeSelect)) {
      return /*#__PURE__*/_jsx(ListingLibTableHeaderRightSlot, {
        children: actionsComponent
      });
    }
    return null;
  }, [actionsComponent, isFirstTimeInApp, shouldRenderUIModeSelect]);
  const pageSizeOptions = pageSizes || PAGE_SIZE_OPTIONS;
  const noRowsSlot = useMemo(() => {
    if (loading) {
      return null;
    }
    return /*#__PURE__*/_jsx(ListingLibFrameworkDataTableEmptyState, {
      renderEmptyState: renderEmptyState,
      emptyStateProps: emptyStateProps
    });
  }, [loading, renderEmptyState, emptyStateProps]);

  /**
   * Notify empty state changes for apps listening to EMPTY_STATE_CHANGE event
   */
  useEffect(() => {
    if (hasEmptyStateRef.current === emptyStateProps.hasEmptyState || emptyStateProps.isPending) {
      return;
    }
    listingLibObserver.notifyEmptyStateChange(emptyStateProps);
    hasEmptyStateRef.current = emptyStateProps.hasEmptyState;
  }, [emptyStateProps]);
  if (showLoadingSkeleton || loadingTableColumns) {
    return /*#__PURE__*/_jsx(ListingLibFrameworkDataTableSkeleton, {
      hasTopBar: Boolean(headerLeftSlot || headerRightSlot),
      rowProps: rowProps
    });
  }
  const isDefiniteEmptyState = Boolean(emptyStateProps.emptyStateType) && Boolean(emptyStateProps.hasEmptyState);
  // Hide paginator if there's less than a page of assets
  // to avoid overwhelming new users
  // https://docs.google.com/presentation/d/1AvZzqY0FRANNiBxRFqCX0eqEyB4IAy1MtEqw5sVdOnc/edit#slide=id.g2f4ca129328_0_80
  // https://hubspot.slack.com/archives/C021HGE3YNT/p1727287907524059
  const hidePaginator = pageSize === pageSizeOptions[0] && page === 0 && dataToRender && dataToRender.total <= pageSize || isDefiniteEmptyState;
  return /*#__PURE__*/_jsx(FrameworkDataTablePassThroughPropsProvider, {
    columns: columns,
    data: dataToRender,
    maxHeight: maxHeight,
    renderNestedContent: renderNestedContent,
    renderPqlRow: renderPqlRow,
    rowProps: rowProps,
    tableColumns: tableColumns,
    children: /*#__PURE__*/_jsxs(InlineEditProvider, {
      children: [/*#__PURE__*/_jsx(FrameworkDataTable, {
        numberOfFrozenColumns: numberOfFrozenColumns,
        BulkActionsComponent: ListingLibBulkActionsComponent,
        canSelectAllRecords: Boolean(bulkActionProps && bulkActionProps.canSelectAllAssets),
        columns: columns,
        getRowId: getRowId,
        hasGate: isUngatedFor,
        hasScope: hasScope,
        headerLeftSlot: headerLeftSlot,
        headerRightSlot: headerRightSlot,
        onColumnOrderChange: handleColumnOrderChange,
        onColumnWidthChange: handleColumnWidthChange,
        loading: loading,
        onPageIndexChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        onSortsChange: handleSortsChange,
        pageIndex: page,
        pageSize: pageSize,
        pageSizes: pageSizeOptions,
        pageable: !hidePaginator,
        RowComponent: ListingLibRowComponent,
        rows: dataToRender ? dataToRender.results : [],
        selectable: Boolean(bulkActionProps && bulkActionProps.hasBulkActions),
        sorts: sorts,
        totalRowCount: dataToRender ? dataToRender.total : 0,
        noRowsSlot: noRowsSlot,
        condensed: isTableCondensed({
          rowProps
        })
      }), inlineEditTourPopover]
    })
  });
}